/** Plan My Week, in the carousel. The whole feature — there is no second surface.
 *
 *  It replaced a right-hand slide-over that covered the map, which is where the
 *  route is: a rep could see a stop or see where it was, never both.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './SalesPlanView.css';
import { siftApiClient } from '../../api/siftApiClient';
import { SalesPlanCards } from './SalesPlanCards';
import { SalesPlanWhy } from './SalesPlanWhy';
import { isLiveDiff, isPreview, mergeForDisplay, prebuildDone, SPREAD_PRESETS, spreadPresetOf, todayEt, weekOptions } from './salesPlanFormat';
import { extractRepChoices, describeError } from './salesPlanErrors';
import { NAV_PREFS, NAV_PREF_LABEL, getNavPref, setNavPref } from '../../maps/navPreference';
import type { NavPref } from '../../maps/navPreference';
import type {
  PlanCapacity,
  PlanEdit,
  PlannedStop,
  PlanSettings,
  SalesPlanCurrentResponse,
  SalesPlanRecapResponse,
} from '../../api/types';
import { track } from '../../telemetry/usage';

export interface SalesPlanViewProps {
  impersonateRole?: string;
  /** Taken from the CSE rep filter at the top of the view. There is no second rep
   *  control: two ways to choose the same thing is two things to keep in step. */
  rep?: string;
  /** Every rep this user may follow. Consumed by the email settings the board renders,
   *  and accepted here so the board can pass one props bag down. */
  repOptions?: string[];
  selectedClientId?: string | null;
  onSelectClient?: (clientId: string) => void;
  onHoverClient?: (clientId: string | null) => void;
  /** Publishes the rendered VISIT stops so the map can draw the route. */
  onStopsChange?: (stops: PlannedStop[]) => void;
  onStartAgentThread?: (prompt: string, workflow: string) => void;
  /** Close-out from the plan, same control and same handler the board uses. */
  onStatusChange?: (clientId: string, status: string) => void;
  /** Bumped by the parent when a client is added from the board, so the plan reloads. */
  reloadToken?: number;
}

/* Two tabs, not four. "What changed" is REVIEW, not work, so it sits collapsed at the
 * bottom of the plan; the recap is a thing you PASTE, so it is a button rather than a
 * view. Both still exist -- they are different questions (did you follow the plan, vs
 * what did you get done) -- they just no longer compete with the week itself. */
type Tab = 'plan' | 'why' | 'settings';

const TAB_LABEL: Record<Tab, string> = {
  plan: 'The plan',
  why: 'Why this week',
  settings: 'Settings',
};

type CapacityField = {
  key: keyof PlanCapacity; label: string; hint: string; step: number; min: number; max: number;
};

const CAPACITY_FIELDS: CapacityField[] = [
  { key: 'visits_per_week', label: 'Visits a week', hint: 'How many stops you can realistically drive to.', step: 1, min: 0, max: 60 },
  { key: 'calls_per_week', label: 'Calls a week', hint: 'Phone work has its own budget; it does not compete with the drive.', step: 1, min: 0, max: 100 },
  { key: 'visits_per_trip', label: 'Stops per trip', hint: 'A hard cap. Extra stops nearby spill into the next trip, which follows straight on rather than sending you back later.', step: 1, min: 1, max: 12 },
];

/** The four distance settings, behind "Fine-tune". A preset sets all four together, because
 *  set one at a time they contradict each other (a 30 mi "area" around a 15 mi trip). */
const SPREAD_FIELDS: CapacityField[] = [
  { key: 'max_radius_mi', label: 'Trip radius (mi)', hint: 'How far from a trip’s first stop another can be and still belong to it.', step: 5, min: 5, max: 200 },
  { key: 'max_drive_min', label: 'Trip drive (min)', hint: 'The same reach in rush-hour drive time. Both must hold, so a stop inside the radius but across a bridge waits for another trip.', step: 5, min: 10, max: 180 },
  { key: 'urgency_pull_mi', label: 'Urgency pull (mi)', hint: 'How far a trip will reach for an URGENT client. Below the trip radius it does nothing.', step: 5, min: 0, max: 200 },
  { key: 'same_area_mi', label: 'Stay in an area (mi)', hint: 'While work remains this close to the trip just built, the next trip stays there. Above the trip radius, the week can hop between areas.', step: 5, min: 0, max: 100 },
];

export const SalesPlanView: React.FC<SalesPlanViewProps> = ({
  impersonateRole,
  rep,
  selectedClientId,
  onSelectClient,
  onHoverClient,
  onStopsChange,
  onStartAgentThread,
  onStatusChange,
  reloadToken,
}) => {
  const [tab, setTab] = useState<Tab>('plan');
  const [plan, setPlan] = useState<SalesPlanCurrentResponse | null>(null);
  const [recap, setRecap] = useState<SalesPlanRecapResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyClient, setBusyClient] = useState<string | null>(null);
  const [repChoices, setRepChoices] = useState<string[] | null>(null);
  const [settings, setSettings] = useState<PlanSettings | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [capacityDraft, setCapacityDraft] = useState<PlanCapacity | null>(null);
  /** Which email is in flight, so only that button says "Sending…". */
  const [emailing, setEmailing] = useState<'weekly' | 'digest' | null>(null);
  const [emailResult, setEmailResult] = useState<string | null>(null);
  const [showRecap, setShowRecap] = useState(false);
  const [recapDraft, setRecapDraft] = useState<string | null>(null);
  const [whichWeek, setWhichWeek] = useState<'current' | 'next'>('current');
  // Device-level (APP-1): navigation happens on the phone, and two phones can differ.
  const [navApp, setNavAppState] = useState<NavPref>(() => getNavPref());
  const chooseNavApp = useCallback((app: NavPref) => {
    setNavPref(app);
    setNavAppState(app);
    track('sales_view', 'nav', 'set_app', {}, { app });
  }, []);

  const weeks = useMemo(() => weekOptions(todayEt()), []);
  const week = whichWeek === 'next' ? weeks.next : weeks.current;

  const params = useMemo(
    () => ({ rep, week, impersonate_role: impersonateRole }),
    [rep, week, impersonateRole],
  );

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const current = await siftApiClient.getSalesPlanCurrent(params, signal);
      if (signal?.aborted) return;
      setPlan(current);
      setSettings(current.settings);
      setRepChoices(null);
    } catch (err) {
      if (signal?.aborted) return;
      const choice = extractRepChoices(err);
      if (choice) {
        // Above rep level the backend refuses to blend territories and names the reps instead.
        // That is a CHOICE to offer, not an error to display.
        setRepChoices(choice.reps);
        setError(choice.message);
        setPlan(null);
        // Settings still have to be reachable. They ride on the plan response, so without
        // this a manager -- who never gets one until they pick a rep -- could not find the
        // subscription controls that are the whole point of the feature for them.
        try {
          setSettings(await siftApiClient.getSalesPlanSettings(
            { impersonate_role: impersonateRole }, signal,
          ));
        } catch {
          // The picker is still useful without them.
        }
      } else {
        setError(describeError(err, 'Could not load the plan'));
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [params, impersonateRole]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, reloadToken]);

  const stops = useMemo(() => plan?.actual.stops ?? [], [plan]);
  /* ONE list: the recommendation and the overrides together, so a dropped stop stays
   * visible and greyed instead of vanishing into a second table. */
  const display = useMemo(
    () => mergeForDisplay(plan?.recommended, plan?.actual, plan?.updates ?? []),
    [plan],
  );
  useEffect(() => { onStopsChange?.(stops); }, [stops, onStopsChange]);

  /* One edit at a time. `busyClient` disables the buttons, but only after a re-render; this
   * is current on the very next click. */
  const editingRef = useRef(false);
  const edit = useCallback(async (change: Omit<PlanEdit, 'source'>) => {
    if (editingRef.current) return;
    editingRef.current = true;
    // A trip-start edit names a trip, not a client; something must still mark the list busy.
    setBusyClient(change.client_id || `trip:${change.to_trip ?? ''}`);
    try {
      // `list_edit` and never inferred: a rep moving a stop leaves no transcript, so this is the
      // only record that the change was theirs rather than the agent's.
      await siftApiClient.addSalesPlanEdit({ ...change, source: 'list_edit' } as PlanEdit, params);
      setRecap(null);
      await load();
    } catch (err) {
      setError(describeError(err, 'Could not save that change'));
    } finally {
      editingRef.current = false;
      setBusyClient(null);
    }
  }, [params, load]);

  const setAccountMode = useCallback(async (clientId: string, mode: 'VISIT' | 'CALL' | null) => {
    if (editingRef.current) return;
    editingRef.current = true;
    setBusyClient(clientId);
    try {
      const out = await siftApiClient.setSalesPlanAccountMode(clientId, mode, params);
      setSettings((prev) => (prev ? { ...prev, account_modes: out.account_modes } : prev));
      setRecap(null);
      await load();
    } catch (err) {
      setError(describeError(err, 'Could not save that account setting'));
    } finally {
      editingRef.current = false;
      setBusyClient(null);
    }
  }, [params, load]);

  const findNearby = useCallback(async (tripNo: number | null) => {
    const found = await siftApiClient.getSalesPlanNearby(
      { ...params, ...(tripNo === null ? {} : { trip: tripNo }) },
    );
    return found.clients;
  }, [params]);

  const handleStatus = useCallback((clientId: string, status: string) => {
    onStatusChange?.(clientId, status);
    // Completion is rendered from the plan payload, so the tick only appears on a reload.
    void load();
  }, [onStatusChange, load]);

  const resetEdits = useCallback(async () => {
    try {
      await siftApiClient.resetSalesPlanEdits(params);
      setRecap(null);
      await load();
    } catch (err) {
      setError(describeError(err, 'Could not reset the plan'));
    }
  }, [params, load]);

  const saveSetting = useCallback(async (key: string, value: unknown) => {
    setSavingKey(key);
    try {
      const next = await siftApiClient.setSalesPlanSetting(
        key as 'weekly_email' | 'daily_digest' | 'capacity'
          | 'subscribed_reps' | 'default_trip_start',
        value, params,
      );
      setSettings(next);
      if (key === 'capacity' || key === 'default_trip_start') {
        setCapacityDraft(null);
        await load();
      }
    } catch (err) {
      setError(describeError(err, 'Could not save that setting'));
    } finally {
      setSavingKey(null);
    }
  }, [params, load]);

  /* The emails are composed the same way at send time, so a chosen start reaches them too. */
  const setTripStart = useCallback((tripNo: number, start: string) => {
    void edit({ action: 'trip_start', client_id: '', to_trip: tripNo, start });
  }, [edit]);

  const [addressDraft, setAddressDraft] = useState({ label: '', address: '' });
  const [addressError, setAddressError] = useState<string | null>(null);
  const addStartAddress = useCallback(async () => {
    setSavingKey('start_addresses');
    setAddressError(null);
    try {
      const out = await siftApiClient.addSalesPlanStartAddress(addressDraft, params);
      setSettings((prev) => (prev ? { ...prev, start_addresses: out.start_addresses } : prev));
      setAddressDraft({ label: '', address: '' });
    } catch (err) {
      setAddressError(describeError(err, 'Could not save that address'));
    } finally {
      setSavingKey(null);
    }
  }, [addressDraft, params]);

  const removeStartAddress = useCallback(async (id: string) => {
    setSavingKey('start_addresses');
    try {
      await siftApiClient.removeSalesPlanStartAddress(id, params);
      // Removing the default start resets it, and trips using it re-price: reload both.
      await load();
    } catch (err) {
      setAddressError(describeError(err, 'Could not remove that address'));
    } finally {
      setSavingKey(null);
    }
  }, [params, load]);

  /* The kind is CHOSEN, not inferred from whether a plan email already went out. One button
   * quietly sending a different email depending on invisible history cannot be predicted, and
   * it would leave no way to ask for the plan again -- which is what a rep does when they are
   * outside a dealership looking for the next address. */
  const emailPlan = useCallback(async (kind: 'weekly' | 'digest') => {
    setEmailing(kind);
    setEmailResult(null);
    try {
      const out = await siftApiClient.emailSalesPlan({ ...params, kind });
      // The backend answers "not sent, and here is why" rather than throwing, because a
      // sandboxed account or an unverified address is a fact the rep can act on.
      setEmailResult(out.sent ? `Sent to ${out.recipient}.` : out.reason ?? 'Not sent.');
    } catch (err) {
      setEmailResult(describeError(err, 'Could not send it.'));
    } finally {
      setEmailing(null);
    }
  }, [params]);

  const openRecap = useCallback(async () => {
    setShowRecap(true);
    if (recap) return;
    try {
      setRecap(await siftApiClient.getSalesPlanRecap(params));
    } catch (err) {
      setEmailResult(describeError(err, 'Could not build the recap.'));
      setShowRecap(false);
    }
  }, [recap, params]);

  const startThread = useCallback(async () => {
    try {
      const kickoff = await siftApiClient.getSalesPlanKickoff(params);
      onStartAgentThread?.(kickoff.prompt, kickoff.workflow);
    } catch {
      onStartAgentThread?.('Review my plan for this week.', 'plan_my_week');
    }
  }, [params, onStartAgentThread]);

  const live = isLiveDiff(plan?.source);
  const preview = isPreview(plan?.source);
  const capacity = capacityDraft ?? settings?.capacity ?? plan?.actual.capacity ?? null;
  const visits = stops.filter((s) => s.contact_mode === 'VISIT').length;

  const pickWeek = (which: 'current' | 'next') => {
    if (which === whichWeek) return;
    setWhichWeek(which);
    setRecap(null);   // cached per week, and openRecap short-circuits on a hit
    setRecapDraft(null);
  };

  return (
    <div className="spv-root">
      <div className="spv-head">
        <div className="spv-title">
          <span className="spv-rep">
            {plan ? plan.rep : 'Plan My Week'}
          </span>
          {plan ? (
            <span className="spv-sub">
              week of {plan.plan_week_start} · {visits} visits, {stops.length - visits} calls
            </span>
          ) : null}
        </div>
        <div className="spv-week" role="group" aria-label="Which week">
          {(['current', 'next'] as const).map((w) => (
            <button
              key={w}
              type="button"
              className={`spv-week-btn${whichWeek === w ? ' spv-week-on' : ''}`}
              aria-pressed={whichWeek === w}
              onClick={() => pickWeek(w)}
            >
              {w === 'current' ? 'This week' : 'Next week'}
            </button>
          ))}
        </div>
        <div className="spv-head-actions">
          <button
            className="spv-btn"
            onClick={() => emailPlan('weekly')}
            disabled={!plan || emailing !== null}
            title="Email me the whole week: every trip, every stop, in order"
          >
            {emailing === 'weekly' ? 'Sending…' : 'Email me the plan'}
          </button>
          <button
            className="spv-btn"
            onClick={() => emailPlan('digest')}
            disabled={!plan || emailing !== null}
            title="Email me where the week stands against the plan, and what is still open"
          >
            {emailing === 'digest' ? 'Sending…' : 'Email me an update'}
          </button>
          <button className="spv-btn" onClick={() => window.print()} disabled={!plan}>Print</button>
          <button className="spv-btn" onClick={openRecap} disabled={!plan}>
            Manager update
          </button>
          {onStartAgentThread ? (
            <button className="spv-btn spv-btn-primary" onClick={startThread}>Ask Mia</button>
          ) : null}
        </div>
      </div>

      <div className="spv-tabs" role="tablist">
        {(Object.keys(TAB_LABEL) as Tab[]).map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            className="spv-tab"
            onClick={() => setTab(t)}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>

      {emailResult ? <div className="spv-toast">{emailResult}</div> : null}

      <div className="spv-body">
        {repChoices ? (
          <div className="spv-picker">
            <strong>Pick a rep in the CSE Rep filter above</strong>
            <p>
              A plan runs for one rep&rsquo;s territory — blended across territories it cannot be
              routed. {repChoices.length} rep{repChoices.length === 1 ? '' : 's'} in view.
            </p>
          </div>
        ) : error ? <div className="spv-error">{error}</div> : null}

        {tab === 'plan' && !repChoices ? (
          <>
            {/* Only once there IS a plan: with none loaded yet `source` is undefined, which
                reads as "not frozen" and accused every week of failing while it loaded. */}
            {plan ? (
              <div className={`spv-banner ${live ? 'spv-banner-live' : 'spv-banner-record'}`}>
                {preview
                  ? prebuildDone(todayEt())
                    ? 'A preview of next week. It has not been frozen yet — opening it does not '
                      + 'fix it, so it can still move.'
                    : 'A preview of next week, built from today\u2019s book. Friday morning it is '
                      + 'rebuilt and frozen, so anything you plan around now can still change.'
                  : live
                    ? 'This week could not be frozen, so it may shift as your book changes. '
                      + 'Changes are measured against a moving recommendation, not a plan.'
                    : `Plan of record, frozen ${plan.generated_at?.slice(0, 16).replace('T', ' ')}.`}
              </div>
            ) : loading ? (
              <div className="spv-banner spv-banner-record">Building your week…</div>
            ) : null}

            {plan?.actual.review.length ? (
              <div className="spv-review">
                <strong>⚠ Review — {plan.actual.review.length} outside your territory</strong>
                <ul>
                  {plan.actual.review.map((r, i) => (
                    <li key={i}>
                      {String(r.client_name ?? r.client_id)}
                      {r.miles_from_territory_median != null
                        ? ` — ${Math.round(Number(r.miles_from_territory_median))} mi out`
                        : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {plan?.completion?.total ? (
              <div className="spv-progress">
                <span className="spv-progress-done">
                  {plan.completion.by_evidence.confirmed} done
                </span>
                {plan.completion.by_evidence.reported ? (
                  <span
                    className="spv-progress-reported"
                    title="You closed these out; Salesforce has not caught up"
                  >
                    {plan.completion.by_evidence.reported} not yet in Salesforce
                  </span>
                ) : null}
                <span className="spv-progress-open">
                  {plan.completion.by_evidence.none} still open
                </span>
              </div>
            ) : null}

            <SalesPlanCards
              stops={display}
              loading={loading}
              selectedClientId={selectedClientId}
              busyClientId={busyClient}
              onSelect={onSelectClient}
              onHover={onHoverClient}
              onEdit={edit}
              onSetAccountMode={setAccountMode}
              onFindNearby={findNearby}
              onStatusChange={handleStatus}
              trips={plan?.actual.trips}
              driveBasis={plan?.actual.drive_basis}
              startAddresses={settings?.start_addresses}
              onSetTripStart={setTripStart}
              navApp={navApp}
              onChooseNavApp={chooseNavApp}
            />

            {plan?.updates.length ? (
              <button className="spv-btn spv-btn-danger" onClick={resetEdits}>
                Reset to the recommendation ({plan.updates.length})
              </button>
            ) : null}
          </>
        ) : null}

        {tab === 'plan' && !repChoices && plan?.actual.notes.length ? (
          <ul className="spv-notes">
            {plan.actual.notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        ) : null}

        {tab === 'why' && !repChoices ? (
          <SalesPlanWhy
            stops={stops}
            capacity={capacity}
            plan={plan?.actual}
            visitTypeHelp={plan?.visit_type_help}
          />
        ) : null}

        {tab === 'settings' ? (
          <div className="spv-settings">
            {/* Only what shapes THIS rep's week. The mail preferences are the reader's, not
                the rep's, and live once in "My emails" above rather than per panel. */}
            <p className="spv-settings-who">
              Settings for {plan?.rep ?? rep ?? 'this rep'}&rsquo;s week.
            </p>
            <p className="spv-settings-note">
              Every territory is planned automatically on Friday for the week ahead. Mail about
              it is opt-in and lives in &ldquo;My emails&rdquo; above.
            </p>

            <div className="spv-capacity">
              <strong>How much week there is</strong>
              <em>
                Every number is a setting, not a law. A rep covering Wyoming does not get the
                same numbers as one covering Orange County.
              </em>
              {capacity ? (
                <>
                  {CAPACITY_FIELDS.map((f) => (
                    <div className="spv-field" key={String(f.key)}>
                      <label htmlFor={`cap-${String(f.key)}`}>{f.label}</label>
                      <input
                        id={`cap-${String(f.key)}`}
                        type="number"
                        step={f.step}
                        min={f.min}
                        max={f.max}
                        value={Number(capacity[f.key] ?? 0)}
                        onChange={(e) => setCapacityDraft({
                          ...capacity, [f.key]: Number(e.target.value),
                        })}
                      />
                      <span className="spv-hint">{f.hint}</span>
                    </div>
                  ))}
                  <div className="spv-field">
                    <label htmlFor="cap-spread">How far a trip spreads</label>
                    <select
                      id="cap-spread"
                      value={spreadPresetOf(capacity)}
                      onChange={(e) => {
                        const preset = SPREAD_PRESETS.find((p) => p.id === e.target.value);
                        if (preset) setCapacityDraft({ ...capacity, ...preset.values });
                      }}
                    >
                      {SPREAD_PRESETS.map((p) => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                      {spreadPresetOf(capacity) === 'custom' ? (
                        <option value="custom" disabled>Custom (fine-tuned below)</option>
                      ) : null}
                    </select>
                    <span className="spv-hint">
                      {SPREAD_PRESETS.find((p) => p.id === spreadPresetOf(capacity))?.hint
                        ?? 'Set by hand below. Pick a preset to put all four back in step.'}
                    </span>
                  </div>
                  <details className="spv-finetune">
                    <summary>Fine-tune distances</summary>
                    {SPREAD_FIELDS.map((f) => (
                      <div className="spv-field" key={String(f.key)}>
                        <label htmlFor={`cap-${String(f.key)}`}>{f.label}</label>
                        <input
                          id={`cap-${String(f.key)}`}
                          type="number"
                          step={f.step}
                          min={f.min}
                          max={f.max}
                          value={Number(capacity[f.key] ?? 0)}
                          onChange={(e) => setCapacityDraft({
                            ...capacity, [f.key]: Number(e.target.value),
                          })}
                        />
                        <span className="spv-hint">{f.hint}</span>
                      </div>
                    ))}
                    {Number(capacity.same_area_mi ?? 0) > Number(capacity.max_radius_mi) ? (
                      <span className="spv-warn" role="note">
                        “Stay in an area” is wider than the trip radius, so the week can hop
                        between areas and come back.
                      </span>
                    ) : null}
                  </details>
                  <div className="spv-field">
                    <label htmlFor="cap-health">
                      Balance: {Math.round((capacity.health_weight ?? 0.5) * 100)}% health
                    </label>
                    <input
                      id="cap-health"
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={Math.round((capacity.health_weight ?? 0.5) * 100)}
                      onChange={(e) => setCapacityDraft({
                        ...capacity, health_weight: Number(e.target.value) / 100,
                      })}
                    />
                    <span className="spv-hint">
                      Left favours who is overdue a touch, right favours whose business is
                      slipping. Neither is right for every book.
                    </span>
                  </div>
                  <div className="spv-field">
                    <label htmlFor="cap-anchor">Build trips around</label>
                    <select
                      id="cap-anchor"
                      value={capacity.trip_anchor ?? 'priority'}
                      onChange={(e) => setCapacityDraft({
                        ...capacity, trip_anchor: e.target.value as 'priority' | 'pipeline',
                      })}
                    >
                      <option value="priority">Highest-priority clients</option>
                      <option value="pipeline">Open pipeline</option>
                    </select>
                    <span className="spv-hint">
                      Open pipeline anchors each trip on a client with a deal off track, a
                      delivery at risk, or a close within 30 days, then fills around it.
                    </span>
                  </div>
                  <button
                    className="spv-btn spv-btn-primary"
                    disabled={!capacityDraft || savingKey === 'capacity'}
                    onClick={() => saveSetting('capacity', capacityDraft)}
                  >
                    {savingKey === 'capacity' ? 'Saving…' : 'Save and rebuild'}
                  </button>
                </>
              ) : <div className="spv-empty">Loading settings…</div>}
            </div>

            <div className="spv-capacity">
              <strong>Accounts fixed as a call or a visit</strong>
              <em>
                Set with 📌 on a plan card. The planner never drives you to a call-only account,
                and always treats a fixed visit as a visit.
              </em>
              {Object.keys(settings?.account_modes ?? {}).length ? (
                <ul className="spv-fixed-list">
                  {Object.entries(settings?.account_modes ?? {}).map(([cid, mode]) => (
                    <li key={cid}>
                      <span>{stops.find((s) => s.client_id === cid)?.client_name ?? cid}</span>
                      <span className="spv-hint">always a {mode === 'CALL' ? 'call' : 'visit'}</span>
                      <button
                        className="spv-btn"
                        disabled={busyClient === cid}
                        onClick={() => void setAccountMode(cid, null)}
                      >
                        Clear
                      </button>
                    </li>
                  ))}
                </ul>
              ) : <span className="spv-hint">None yet.</span>}
            </div>

            <div className="spv-capacity">
              <strong>Take Me There</strong>
              <em>
                Take Me There opens this app. &ldquo;My phone&rsquo;s default&rdquo; lets an
                Android phone pick (or ask), and asks once on an iPhone. Saved on this device, so
                your phone can use a different app. The plan emails link every app.
              </em>
              <div className="spv-field">
                <label htmlFor="nav-app">Navigate with</label>
                <select
                  id="nav-app"
                  value={navApp}
                  onChange={(e) => chooseNavApp(e.target.value as NavPref)}
                >
                  {NAV_PREFS.map((app) => (
                    <option key={app} value={app}>{NAV_PREF_LABEL[app]}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="spv-capacity">
              <strong>Where trips start</strong>
              <em>
                Drive times are estimated from typical weekday rush-hour traffic, and run from
                here to the first stop. You can change any single trip on the plan.
              </em>
              <div className="spv-field">
                <label htmlFor="trip-start-default">Start each trip from</label>
                <select
                  id="trip-start-default"
                  value={settings?.default_trip_start ?? 'anchor'}
                  disabled={!settings || savingKey === 'default_trip_start'}
                  onChange={(e) => void saveSetting('default_trip_start', e.target.value)}
                >
                  <option value="anchor">The trip&rsquo;s anchor client</option>
                  <option value="previous">The previous trip&rsquo;s last stop</option>
                  {(settings?.start_addresses ?? []).map((a) => (
                    <option key={a.id} value={`address:${a.id}`}>{a.label}</option>
                  ))}
                </select>
                <span className="spv-hint">
                  From a saved address, the drive back is counted too.
                </span>
              </div>
              {(settings?.start_addresses ?? []).length ? (
                <ul className="spv-fixed-list">
                  {(settings?.start_addresses ?? []).map((a) => (
                    <li key={a.id}>
                      <span>{a.label}</span>
                      <span className="spv-hint" title="The address we matched">{a.address}</span>
                      <button
                        className="spv-btn"
                        disabled={savingKey === 'start_addresses'}
                        onClick={() => void removeStartAddress(a.id)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {(settings?.start_addresses ?? []).length < 5 ? (
                <div className="spv-field">
                  <label htmlFor="start-label">Add a starting address</label>
                  <input
                    id="start-label"
                    placeholder="Name, e.g. Home"
                    maxLength={40}
                    value={addressDraft.label}
                    onChange={(e) => setAddressDraft((d) => ({ ...d, label: e.target.value }))}
                  />
                  <input
                    aria-label="Street address"
                    placeholder="Street, city, state"
                    maxLength={200}
                    value={addressDraft.address}
                    onChange={(e) => setAddressDraft((d) => ({ ...d, address: e.target.value }))}
                  />
                  <button
                    className="spv-btn"
                    disabled={
                      !addressDraft.label.trim() || addressDraft.address.trim().length < 5
                      || savingKey === 'start_addresses'
                    }
                    onClick={() => void addStartAddress()}
                  >
                    {savingKey === 'start_addresses' ? 'Finding it…' : 'Add'}
                  </button>
                  {addressError ? <span className="spv-error">{addressError}</span> : (
                    <span className="spv-hint">Check the matched address shown after saving.</span>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {showRecap ? (
        <div className="spv-modal" role="dialog" aria-modal="true" aria-label="Manager update">
          <div className="spv-modal-card">
            <div className="spv-modal-head">
              <strong>Manager update</strong>
              <button className="spv-btn" onClick={() => setShowRecap(false)}>Close</button>
            </div>
            {recap ? (
              <>
                <p className="spv-hint">
                  Edit it before you send it — work Salesforce has not caught up with is
                  listed separately and labelled, on purpose.
                </p>
                <textarea
                  className="spv-modal-text"
                  defaultValue={recap.text}
                  onChange={(e) => setRecapDraft(e.target.value)}
                  rows={14}
                />
                <button
                  className="spv-btn spv-btn-primary"
                  onClick={() => {
                    void navigator.clipboard?.writeText(recapDraft ?? recap.text);
                    setEmailResult('Copied.');
                    setShowRecap(false);
                  }}
                >
                  Copy
                </button>
              </>
            ) : <div className="spv-empty">Building it…</div>}
          </div>
        </div>
      ) : null}
    </div>
  );
};
